import {Component, ViewChild} from '@angular/core';
import {MdbSidenavComponent} from 'mdb-angular-ui-kit/sidenav';
import moment from 'moment';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {NIGHTWEB_APP_CONSTANT} from 'src/app/constants';
import {DownloadService, ThreatPairService, NotificationService} from 'src/app/services';
import { getDistanceParameterData, getDistanceParameterLabel } from 'src/app/utils';

export type AlertChannelVariant = 'push' | 'email' | 'sms';
export type ThreatPairAlertStatus = 'sent' | 'received' | 'seen' | 'failed';

export interface ThreatPairAlertChannel {
  label: string;
  variant: AlertChannelVariant;
}

export interface ThreatPairAlert {
  id: string;
  timestampLabel: string;
  sortDate: string;
  distance: string;
  thresholdLabel?: string;
  channels: ThreatPairAlertChannel[];
  survivorStatus: ThreatPairAlertStatus;
  perpetratorStatus: ThreatPairAlertStatus;
  watchNotificationKey: string | null;
  distanceParameter: string;
  zone: string;
  /** Socket thresholdLabel is already in display units; API thresholdAtTime is meters. */
  thresholdInDisplayUnits?: boolean;
}

@Component({
  selector: 'app-threat-pair-alert-log',
  templateUrl: './threat-pair-alert-log.component.html',
  styleUrls: ['./threat-pair-alert-log.component.scss'],
  standalone: false
})
export class ThreatPairAlertLogComponent {
  pair: any;
  distanceParameter: string = 'meter';

  @ViewChild('sidenav', {static: false}) sidenav!: MdbSidenavComponent;

  /** Loaded pages (template reads length for loading UI). */
  allAlerts: ThreatPairAlert[] = [];
  displayedAlerts: ThreatPairAlert[] = [];

  filterFrom = '';
  filterTo = '';
  /** First page load */
  alertsLoading = false;
  /** Additional pages (infinite scroll) */
  loadingMore = false;
  downloading = false;

  private readonly limit = NIGHTWEB_APP_CONSTANT.defaultLimit;
  private offset = NIGHTWEB_APP_CONSTANT.defaultOffset;
  private totalAlertCount = 0;
  private activeThreatPairId: string | null = null;

  private advocateLogSubscription?: Subscription;
  private alertViewedStatusSubscription?: Subscription;

  constructor(
    private threatPairService: ThreatPairService,
    private downloadService: DownloadService,
    private notificationService: NotificationService,
  ) {
  }

  openAlerts(pair: any, distanceParameter: string = 'meter'): void {
    this.pair = pair;
    this.distanceParameter = distanceParameter;
    this.filterFrom = '';
    this.filterTo = '';
    this.allAlerts = [];
    this.totalAlertCount = 0;
    this.offset = NIGHTWEB_APP_CONSTANT.defaultOffset;
    this.activeThreatPairId = pair?.id ? String(pair.id) : null;
    this.syncDisplayedAlerts();
    this.sidenav.toggle();

    if (!this.activeThreatPairId) {
      return;
    }

    this.fetchAlertsPage(false);

    this.stopAdvocateLogListen();
    this.stopAlertViewedStatusSubscriptionListen();
    this.notificationService.joinRoom('threat-pair-join', this.activeThreatPairId);
    this.advocateLogSubscription = this.notificationService
      .listen('advocate-logs')
      .subscribe((payload: any) => {
        this.allAlerts = [this.mapAdvocateLog(payload), ...this.allAlerts];
        this.syncDisplayedAlerts();
      });
    this.alertViewedStatusSubscription = this.notificationService
      .listen('alert-viewed-status-update')
      .subscribe((payload: any) => {
        this.handleAlertViewedStatusUpdate(payload);
      });
  }

  onClose(): void {
    this.notificationService.leaveRoom('threat-pair-leave', this.activeThreatPairId ?? '');
    this.stopAdvocateLogListen();
    this.stopAlertViewedStatusSubscriptionListen();
    this.sidenav.toggle();
    this.activeThreatPairId = null;
    this.allAlerts = [];
    this.downloading = false;
    this.syncDisplayedAlerts();
  }

  ngOnDestroy(): void {
    this.stopAdvocateLogListen();
    this.stopAlertViewedStatusSubscriptionListen();
  }

  private stopAdvocateLogListen(): void {
    this.advocateLogSubscription?.unsubscribe();
    this.advocateLogSubscription = undefined;
  }

  private stopAlertViewedStatusSubscriptionListen(): void {
    this.alertViewedStatusSubscription?.unsubscribe();
    this.alertViewedStatusSubscription = undefined;
  }

  private mapAdvocateLog(payload: any): ThreatPairAlert {
    const raw = Array.isArray(payload) ? payload[0] : payload;
    const item = raw?.data ?? raw;
    const d = new Date();
    const dist = item?.distance ? Number(item.distance).toFixed(3) : item?.detectedDistance ? Number(item.detectedDistance).toFixed(3) : '';
    const th = item?.thresholdLabel ?? item?.thresholdAtTime;
    return {
      id: String(item?.id ?? `live-${d.getTime()}`),
      timestampLabel: d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      sortDate: d.toISOString().slice(0, 10),
      distance: dist != null ? `${dist}` : '',
      thresholdLabel: typeof th === 'number' ? `${th}` : (typeof th === 'string' ? th : undefined),
      thresholdInDisplayUnits: true,
      channels: [],
      survivorStatus: item.survivorStatus ?? 'sent',
      perpetratorStatus: item.perpetratorStatus ?? 'sent',
      watchNotificationKey: item.watchNotificationKey ?? null,
      distanceParameter: item.distanceParameter ?? 'meter',
      zone: item.zone ?? 'unknown',
    };
  }

  private handleAlertViewedStatusUpdate(payload: any): void {
    const raw = Array.isArray(payload) ? payload[0] : payload;
    const item = raw?.data ?? raw;

    const status = item?.status;
    const notificationKey = item?.watchNotificationKey;
    const isSurvivor = item?.isSurvivor === true;

    if (
      notificationKey == null ||
      notificationKey === '' ||
      !['sent', 'received', 'seen', 'failed'].includes(status)
    ) {
      return;
    }

    const alertIndex = this.allAlerts.findIndex(
      (alert) => String(alert.watchNotificationKey) === String(notificationKey),
    );

    if (alertIndex < 0) {
      return;
    }

    const alert = this.allAlerts[alertIndex];
    this.allAlerts[alertIndex] = {
      ...alert,
      ...(isSurvivor
        ? {survivorStatus: status as ThreatPairAlertStatus}
        : {perpetratorStatus: status as ThreatPairAlertStatus}),
    };
    this.syncDisplayedAlerts();
  }

  onAlertsScroll(event: Event): void {
    if (this.alertsLoading || this.loadingMore) {
      return;
    }
    const el = event.target as HTMLElement;
    const height = Math.round(el.clientHeight + el.scrollTop + 1);
    if (height >= el.scrollHeight) {
      this.onScrollToBottom();
    }
  }

  private onScrollToBottom(): void {
    if (this.allAlerts.length >= this.totalAlertCount) {
      return;
    }
    this.offset += 1;
    this.fetchAlertsPage(true);
  }

  private fetchAlertsPage(append: boolean): void {
    const id = this.activeThreatPairId;
    if (!id) {
      return;
    }

    if (append) {
      this.loadingMore = true;
    } else {
      this.alertsLoading = true;
    }

    const pageOffset = append
      ? this.offset
      : NIGHTWEB_APP_CONSTANT.defaultOffset;
    const params = this.buildAlertRequestParams(pageOffset);

    this.threatPairService
      .getThreatPairAlerts(id, params)
      .pipe(
        finalize(() => {
          this.alertsLoading = false;
          this.loadingMore = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const data = response?.data;
          const rows = data?.rows ?? [];
          const mapped = rows.map((item: any) => this.mapRow(item));
          this.allAlerts = append ? [...this.allAlerts, ...mapped] : mapped;

          const count = data?.count;
          if (count != null) {
            this.totalAlertCount = count;
          } else if (rows.length < this.limit) {
            this.totalAlertCount = this.allAlerts.length;
          } else {
            this.totalAlertCount = Number.POSITIVE_INFINITY;
          }

          this.syncDisplayedAlerts();
        },
        error: () => {
          if (!append) {
            this.allAlerts = [];
            this.totalAlertCount = 0;
          }
          this.syncDisplayedAlerts();
        },
      });
  }

  private buildAlertRequestParams(offset: number): {
    limit: number;
    offset: number;
    fromDate?: string;
    toDate?: string;
  } {
    const params: {
      limit: number;
      offset: number;
      fromDate?: string;
      toDate?: string;
    } = {limit: this.limit, offset};
    const from = this.filterFrom?.trim();
    const to = this.filterTo?.trim();
    if (from) {
      params.fromDate = this.filterDateToApiFormat(from);
    }
    if (to) {
      params.toDate = this.filterDateToApiFormat(to);
    }
    return params;
  }

  private filterDateToApiFormat(ymd: string): string {
    const m = moment(ymd, 'YYYY-MM-DD', true);
    return m.isValid()
      ? m.format(NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat)
      : ymd;
  }

  get pairTitle(): string {
    if (!this.pair?.survivor || !this.pair?.perpetrator) {
      return '';
    }
    const names = `${this.pair.survivor.name} ↔ ${this.pair.perpetrator.name}`;
    const t = this.pair?.distanceThreshold;
    return t != null ? `${names} · Threshold: ${t} ${getDistanceParameterLabel(this.distanceParameter)}` : names;
  }

  get thresholdDisplay(): string {
    const t = this.pair?.distanceThreshold;
    return t != null ? `${t} m` : '150 m';
  }

  breachMessage(alert: ThreatPairAlert): string {
    const unit = getDistanceParameterLabel(this.distanceParameter);
    const distanceMeters = alert.distance != null && alert.distance !== '—'
      ? Number(alert.distance)
      : NaN;
    const thresholdMeters = alert.thresholdLabel != null
      ? Number(alert.thresholdLabel)
      : NaN;
  
    const distance = Number.isFinite(distanceMeters)
      ? getDistanceParameterData(distanceMeters, this.distanceParameter)
      : NaN;

    const thresholdRaw = alert.thresholdLabel != null ? Number(alert.thresholdLabel) : NaN;

    const threshold = Number.isFinite(thresholdRaw) ? alert.thresholdInDisplayUnits ? thresholdRaw
      : getDistanceParameterData(thresholdMeters, this.distanceParameter)
      : NaN;
  
    const distanceText = Number.isFinite(distance) ? distance.toFixed(3) : '—';
    const thresholdText = Number.isFinite(threshold) ? String(threshold) : '—';
    let breachText = "";
    if(alert.zone === 'red' || alert.zone === 'yellow') {
      breachText = "- Threshold breached";
    }
  
    return `${distanceText} ${unit} detected ${breachText} (${thresholdText} ${unit})`;
  }

  notificationStatusIcon(status: ThreatPairAlertStatus): string {
    switch (status) {
      case 'seen':
        return 'fa-bell fa-shake';
      case 'failed':
        return 'fa-triangle-exclamation';
      case 'received':
        return 'fa-check-double';
      case 'sent':
      default:
        return 'fa-check';
    }
  }

  notificationStatusClass(status: ThreatPairAlertStatus): string {
    switch (status) {
      case 'sent':
        return 'notification-status-sent';
      case 'received':
        return 'notification-status-received';
      case 'seen':
        return 'notification-status-seen';
      case 'failed':
        return 'notification-status-failed';
      default:
        return 'notification-status-sent';
    }
  }

  notificationStatusLabel(status: ThreatPairAlertStatus): string {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'received':
        return 'Received';
      case 'seen':
        return 'Alerted';
      case 'failed':
        return 'Failed';
      default:
        return 'Sent';
    }
  }

  channelBadgeClasses(variant: AlertChannelVariant): string[] {
    switch (variant) {
      case 'push':
        return ['bg-warning', 'bg-opacity-25', 'text-dark'];
      case 'email':
        return ['bg-info', 'bg-opacity-10', 'text-info'];
      case 'sms':
        return ['bg-accent-1', 'text-dark'];
      default:
        return ['bg-secondary', 'bg-opacity-10', 'text-secondary'];
    }
  }

  getNotificationStatusTooltip(status: ThreatPairAlertStatus): string {
    return `<span class="notification-tooltip-status notification-tooltip-status--${status}">
    ${this.notificationStatusLabel(status)}
  </span>`;
  }

  /** Filter enabled when not loading and each field is empty or a valid yyyy-MM-dd (native date input). */
  get filterButtonEnabled(): boolean {
    if (this.alertsLoading || this.loadingMore || this.downloading) {
      return false;
    }
    return (
      this.isEmptyOrValidIsoDate(this.filterFrom) &&
      this.isEmptyOrValidIsoDate(this.filterTo)
    );
  }

  private isEmptyOrValidIsoDate(value: string | null | undefined): boolean {
    const v = value?.trim() ?? '';
    if (!v) {
      return true;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return false;
    }
    const [y, m, d] = v.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return (
      dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
    );
  }

  /** Filter button: server applies fromDate / toDate; reset list and reload page 1. */
  applyAlertsFilter(): void {
    if (!this.activeThreatPairId || !this.filterButtonEnabled) {
      return;
    }
    this.offset = NIGHTWEB_APP_CONSTANT.defaultOffset;
    this.allAlerts = [];
    this.totalAlertCount = 0;
    this.syncDisplayedAlerts();
    this.fetchAlertsPage(false);
  }

  get downloadButtonEnabled(): boolean {
    return (
      !!this.activeThreatPairId &&
      !this.alertsLoading &&
      !this.loadingMore &&
      !this.downloading &&
      this.filterButtonEnabled &&
      this.totalAlertCount > 0
    );
  }

  /** Download all alerts in the active filter range as JSON (not only scrolled pages). */
  downloadAlertsJson(): void {
    const id = this.activeThreatPairId;
    if (!id || !this.downloadButtonEnabled) {
      return;
    }

    this.downloading = true;

    const limit =
      Number.isFinite(this.totalAlertCount) && this.totalAlertCount > 0
        ? this.totalAlertCount
        : 10_000;

    const params = {
      ...this.buildAlertRequestParams(NIGHTWEB_APP_CONSTANT.defaultOffset),
      limit,
    };

    this.threatPairService
      .getThreatPairAlerts(id, params)
      .pipe(finalize(() => (this.downloading = false)))
      .subscribe({
        next: (response: any) => {
          const rows = response?.data?.rows ?? [];
          const payload = rows.map((item: any) => this.mapRowForExport(item));
          const from = this.filterFrom?.trim() || 'start';
          const to = this.filterTo?.trim() || 'end';
          this.downloadService.downloadJson(
            payload,
            `threat-pair-alerts_${id}_${from}_${to}.json`,
          );
        },
      });
  }

  private syncDisplayedAlerts(): void {
    this.displayedAlerts = [...this.allAlerts];
  }

  private mapRowForExport(item: any) {
    const alert = this.mapRow(item);
    const detectedMeters = item?.detectedDistance != null ? Number(item.detectedDistance) : NaN;
    return {
      id: item?.id ?? null,
      createdAt: item?.createdAt ?? null,
      detectedDistance: Number.isFinite(detectedMeters)
      ? getDistanceParameterData(detectedMeters, this.distanceParameter)
      : null,
      thresholdAtTime: item?.thresholdAtTime ?? null,
      survivorNotified: item?.survivorNotified !== false,
      perpetratorNotified: item?.perpetratorNotified === true,
      message: this.breachMessage(alert),
      notifications: {
        push: !!item?.notifications?.push?.sent,
        email: !!item?.notifications?.email?.sent,
        sms: !!item?.notifications?.sms?.sent,
      },
    };
  }

  private mapRow(item: any): ThreatPairAlert {
    const d = item?.createdAt ? new Date(item.createdAt) : null;
    const ok = !!(d && !Number.isNaN(d.getTime()));

    const channels: ThreatPairAlertChannel[] = [];
    const n = item?.notifications;
    if (n?.push?.sent) {
      channels.push({label: 'Push', variant: 'push'});
    }
    if (n?.email?.sent) {
      channels.push({label: 'Email', variant: 'email'});
    }
    if (n?.sms?.sent) {
      channels.push({label: 'SMS', variant: 'sms'});
    }

    const dist = item?.detectedDistance;
    const th = item?.thresholdAtTime;

    return {
      id: String(item?.id ?? ''),
      timestampLabel: ok
        ? d!.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
        : '—',
      sortDate: ok ? d!.toISOString().slice(0, 10) : '',
      distance: dist != null ? `${dist}` : '—',
      thresholdLabel: typeof th === 'number' ? `${th}` : undefined,
      channels,
      survivorStatus: item.survivorStatus ?? 'sent',
      perpetratorStatus: item.perpetratorStatus ?? 'sent',
      watchNotificationKey: item.watchNotificationKey ?? null,
      distanceParameter: item.distanceParameter ?? 'meter',
      zone: item.zone ?? 'unknown',
    };
  }

  isThresholdBreached(alert: ThreatPairAlert): boolean {
    return alert.zone === 'red' || alert.zone === 'yellow';
  }
}
